'use client'

import { cn } from '@brew/ui/cn'
import { useAuth, useClerk } from '@clerk/nextjs'
import type { MentionItem } from '@tigerabrodioss/fude'
import { nanoid } from 'nanoid'
import { useRouter } from 'next/navigation'
import {
  type FormEvent,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react'
import {
  PromptInput,
  PromptInputActionMenuMobile,
  type PromptInputAttachmentInput,
  PromptInputDictationButton,
  PromptInputFooter,
  type PromptInputMentionItem,
  type PromptInputMessage,
  PromptInputProvider,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
} from '@/components/ai-elements/prompt-input'
import {
  ComposerAttachments,
  ComposerSubmitButton,
} from '@/components/chat/composer'
import {
  COMPOSER_PLACEHOLDER_DEFAULT,
  getComposerBrandGatePlaceholder,
} from '@/components/chat/composer-brand-gate'
import {
  buildPendingPrompt,
  hasPendingPrompt,
  writePendingPrompt,
} from '@/components/chat/pending-prompt'
import {
  splitPendingPromptFiles,
  stashPendingPromptFiles,
} from '@/components/chat/pending-prompt-inline-files'
import {
  buildPublicComposerDraft,
  createPublicComposerFilesKey,
  deletePublicComposerDraftFiles,
  toDraftLocalFiles,
  writePublicComposerDraft,
  writePublicComposerDraftFiles,
} from '@/components/chat/public-composer-draft'
import { PENDING_PROMPT_KEY } from '@/components/chat/types'
import {
  LAUNCH_COMPOSER_FRAME_CLASSNAME,
  LAUNCH_COMPOSER_SURFACE_CLASSNAME,
} from '@/components/chat-launch/launch-composer-chrome'
import { useCreditsLimitGuard } from '@/components/upgrade/use-credits-limit-guard'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { usePromptSuggestionContext } from '@/hooks/use-prompt-suggestion-context'

type LaunchComposerMode = 'authenticated' | 'public'

type LaunchComposerProps = {
  orgId?: string
  mode?: LaunchComposerMode
  className?: string
  composerId?: string
  initialInput?: string
  initialModelId?: string
  initialAttachments?: Array<PromptInputAttachmentInput>
  initialPublicChatId?: string
  input?: string
  onInputChange?: (value: string) => void
  onFetchMentions?:
    | ((query: string) => Promise<Array<PromptInputMentionItem>>)
    | ((query: string) => Promise<Array<MentionItem>>)
}

export function LaunchComposer({
  orgId,
  mode = 'authenticated',
  className,
  composerId = 'default',
  initialInput = '',
  initialModelId: _initialModelId,
  initialAttachments = [],
  initialPublicChatId,
  input,
  onInputChange,
  onFetchMentions,
}: LaunchComposerProps) {
  const [publicChatId] = useState(() => initialPublicChatId ?? nanoid())
  const router = useRouter()
  const { isSignedIn, isLoaded } = useAuth()
  const { openSignUp } = useClerk()
  const [uncontrolledInput, setUncontrolledInput] = useState(initialInput)
  const hasRedirectedPending = useRef(false)
  const isControlled = input !== undefined && !!onInputChange
  const currentInput = isControlled ? input : uncontrolledInput
  const isPublicMode = mode === 'public'

  useEffect(() => {
    if (
      isPublicMode ||
      !isLoaded ||
      !isSignedIn ||
      hasRedirectedPending.current
    ) {
      return
    }

    try {
      const stored = localStorage.getItem(PENDING_PROMPT_KEY)
      if (!stored) {
        return
      }

      const pending = JSON.parse(stored) as {
        awaitingAuth?: boolean
        chatId?: string
      }
      if (!pending.awaitingAuth || !pending.chatId) {
        return
      }
      // Only the chat thread clears this slot, so an expired or foreign one
      // names a chat nothing will create — following it would bounce every
      // /home visit to a dead end.
      if (!hasPendingPrompt({ chatId: pending.chatId, orgId: orgId ?? null })) {
        return
      }

      hasRedirectedPending.current = true
      const target = `/chat/${encodeURIComponent(pending.chatId)}`
      startTransition(() => {
        router.push(target)
      })
    } catch {
      // Ignore malformed local storage state.
    }
  }, [isLoaded, isPublicMode, isSignedIn, orgId, router])

  const setComposerInput = useCallback(
    (value: string) => {
      if (isControlled) {
        onInputChange?.(value)
        return
      }

      setUncontrolledInput(value)
    },
    [isControlled, onInputChange]
  )

  return (
    <PromptInputProvider
      initialAttachments={initialAttachments}
      attachmentUploadBehavior={isPublicMode ? 'local' : 'upload'}
    >
      <LaunchComposerInner
        className={className}
        composerId={composerId}
        currentInput={currentInput}
        isPublicMode={isPublicMode}
        isSignedIn={!!isSignedIn}
        onFetchMentions={onFetchMentions}
        onOpenSignUp={openSignUp}
        orgId={orgId}
        publicChatId={publicChatId}
        router={router}
        setComposerInput={setComposerInput}
      />
    </PromptInputProvider>
  )
}

type LaunchComposerInnerProps = {
  className?: string
  composerId: string
  currentInput: string
  isPublicMode: boolean
  isSignedIn: boolean
  onFetchMentions?:
    | ((query: string) => Promise<Array<PromptInputMentionItem>>)
    | ((query: string) => Promise<Array<MentionItem>>)
  onOpenSignUp: ReturnType<typeof useClerk>['openSignUp']
  orgId?: string
  publicChatId: string
  router: ReturnType<typeof useRouter>
  setComposerInput: (value: string) => void
}

function LaunchComposerInner({
  className,
  composerId,
  currentInput,
  isPublicMode,
  isSignedIn,
  onFetchMentions,
  onOpenSignUp,
  orgId,
  publicChatId,
  router,
  setComposerInput,
}: LaunchComposerInnerProps) {
  const brandCtx = useOptionalBrandContext()
  const contextBrandId = brandCtx?.brandId ?? null
  // Submission is scoped to the ACTIVE brand, so gate on ITS readiness: an
  // in-flight brand is selectable everywhere else in the app, but a chat
  // against it would have no design system (and the orchestrator would treat
  // the first turn as an extraction run). `hasReadyBrand` only picks the copy.
  const hasReadyBrand = brandCtx ? brandCtx.hasReadyBrand : true
  const isActiveBrandReady = brandCtx ? brandCtx.isActiveBrandReady : true
  const attachments = usePromptInputAttachments()
  const latestPersistIdRef = useRef(0)
  const activeFilesKeyRef = useRef<string | undefined>(undefined)
  // Credits gate: public mode never has an org snapshot in scope, so the
  // hook returns a pass-through. Authenticated users hit the dialog when
  // out of credits and the orchestrator POST is suppressed.
  const { guard: guardCredits, isOverLimit: isOverCreditLimit } =
    useCreditsLimitGuard()
  // No chat trail on the launch surface — it starts a fresh conversation.
  // Public mode never fetches (enableAutocomplete={false} below).
  const suggestionContext = usePromptSuggestionContext({
    surface: 'launch',
  })

  const persistPublicDraft = useCallback(async () => {
    const persistId = latestPersistIdRef.current + 1
    latestPersistIdRef.current = persistId
    const draftText = currentInput
    const localFiles = toDraftLocalFiles(attachments.files)
    const shouldPersist =
      draftText.trim().length > 0 || localFiles.length > 0 || !!contextBrandId

    if (!shouldPersist) {
      const previousFilesKey = activeFilesKeyRef.current
      if (persistId !== latestPersistIdRef.current) {
        return
      }
      writePublicComposerDraft(null)
      activeFilesKeyRef.current = undefined
      await deletePublicComposerDraftFiles(previousFilesKey)
      return
    }

    const nextFilesKey =
      localFiles.length > 0
        ? createPublicComposerFilesKey(publicChatId)
        : undefined

    if (nextFilesKey) {
      await writePublicComposerDraftFiles(nextFilesKey, localFiles)
    }

    if (persistId !== latestPersistIdRef.current) {
      await deletePublicComposerDraftFiles(nextFilesKey)
      return
    }

    const previousFilesKey = activeFilesKeyRef.current
    writePublicComposerDraft(
      buildPublicComposerDraft({
        chatId: publicChatId,
        text: draftText,
        filesKey: nextFilesKey,
        customBrandId: contextBrandId,
        files: attachments.files,
      })
    )
    activeFilesKeyRef.current = nextFilesKey

    if (previousFilesKey && previousFilesKey !== nextFilesKey) {
      await deletePublicComposerDraftFiles(previousFilesKey)
    }
  }, [attachments.files, contextBrandId, currentInput, publicChatId])

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Ultracite baseline — verify deps when touching this hook
  useEffect(() => {
    if (!isPublicMode) {
      return
    }

    void persistPublicDraft()
  }, [
    currentInput,
    isPublicMode,
    persistPublicDraft,
    contextBrandId,
    attachments.files,
  ])

  const openPublicAuthFlow = useCallback(async () => {
    await persistPublicDraft()

    if (isSignedIn) {
      router.push('/create-organization')
      return
    }

    void onOpenSignUp()
  }, [isSignedIn, onOpenSignUp, persistPublicDraft, router])

  // Pending state of the `/home` → `/chat/<id>` hand-off. A transition, not
  // a sticky boolean: under `cacheComponents` the router parks the last few
  // visited segments in a hidden <Activity> instead of unmounting them, so
  // this composer comes back from the round-trip as the SAME instance. A
  // flag that counted on the unmount to reset it kept the submit button
  // disabled on the next visit. `isNavigating` holds for exactly the gap in
  // which a double-Enter could mint a second chat and clears itself when the
  // navigation commits (or is superseded).
  const [isNavigating, startNavigation] = useTransition()

  const handleSubmit = useCallback(
    async (message: PromptInputMessage, event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      // `false` tells PromptInput the send was declined: the prompt,
      // attachments and any dictation stay as they are.
      if (isNavigating) {
        return false
      }

      const text = message.text?.trim() ?? ''
      const files = message.files ?? []

      if (!text && files.length === 0) {
        return false
      }

      if (isPublicMode) {
        await openPublicAuthFlow()
        return false
      }

      const resolvedOrgId = orgId
      if (!resolvedOrgId) {
        return false
      }

      if (!isActiveBrandReady) {
        return false
      }

      if (!isSignedIn) {
        const chatId = nanoid()
        const split = splitPendingPromptFiles(files)
        const filesKey =
          split.stashed.length > 0
            ? await stashPendingPromptFiles(chatId, split.stashed)
            : undefined
        writePendingPrompt(
          buildPendingPrompt({
            chatId,
            orgId: resolvedOrgId,
            text,
            customBrandId: contextBrandId,
            files: split.stored,
            ...(filesKey ? { filesKey } : {}),
            awaitingAuth: true,
            messageId: crypto.randomUUID(),
          })
        )
        setComposerInput('')
        onOpenSignUp()
        return
      }

      // Credits gate runs before anything else so the upgrade dialog pops
      // synchronously on click (the orchestrator route enforces credits
      // server-side as the safety net).
      let isAllowed = false
      guardCredits(() => {
        isAllowed = true
      })
      if (!isAllowed) {
        return false
      }

      // Client-first hand-off: mint the chatId here, stash the prompt in
      // the pending-prompt slot, and navigate immediately. The chat
      // thread that mounts at `/chat/<id>` consumes the slot, paints the
      // user message optimistically in the same frame, and fires the
      // orchestrator POST — which is the single persister of the user
      // message. No server round-trip on the critical path.
      const chatId = nanoid()
      // Inline email-source files are too large for localStorage: stash the
      // File objects in IndexedDB and let the chat thread re-stage them.
      const split = splitPendingPromptFiles(files)
      // Only an inline file forces the async stash; the plain-text launch
      // stays synchronous so the navigation happens on the click.
      const filesKey =
        split.stashed.length > 0
          ? await stashPendingPromptFiles(chatId, split.stashed)
          : undefined
      writePendingPrompt(
        buildPendingPrompt({
          chatId,
          orgId: resolvedOrgId,
          text,
          customBrandId: contextBrandId,
          files: split.stored,
          ...(filesKey ? { filesKey } : {}),
          messageId: crypto.randomUUID(),
        })
      )
      setComposerInput('')
      // New chats stream on their own full-width `/chat/<id>` page. Nothing is
      // behind that page, so the canvas bridge takes the user to the first
      // artifact the turn builds, once (`artifact-hop.ts`) — the
      // chat rides along in the split panel. No arming needed here: the thread
      // survives the hop, so this is a pure client-side decision made from the
      // tool stream.
      const target = `/chat/${encodeURIComponent(chatId)}`
      startNavigation(() => {
        router.push(target)
      })
    },
    [
      contextBrandId,
      guardCredits,
      isActiveBrandReady,
      isNavigating,
      isPublicMode,
      isSignedIn,
      onOpenSignUp,
      openPublicAuthFlow,
      orgId,
      router,
      setComposerInput,
    ]
  )

  return (
    <div
      className={className}
      data-testid="homepage-prompt-collector"
      data-launch-composer={composerId}
    >
      <div className={LAUNCH_COMPOSER_FRAME_CLASSNAME}>
        <PromptInput
          onSubmit={handleSubmit}
          className={cn(
            'mx-auto mt-0 overflow-hidden border-0 bg-background shadow-none',
            LAUNCH_COMPOSER_SURFACE_CLASSNAME
          )}
          data-testid="prompt-input"
          attachmentUploadBehavior={isPublicMode ? 'local' : 'upload'}
          clearAttachmentsOnSubmit={!isPublicMode}
        >
          <ComposerAttachments />
          <PromptInputTextarea
            shimmerPlaceholder
            disabled={!isPublicMode && !isActiveBrandReady}
            enableAutocomplete={!isPublicMode}
            getSuggestionContext={suggestionContext.getContext}
            onChange={(e) => setComposerInput(e.currentTarget.value)}
            onFetchMentions={onFetchMentions}
            placeholder={
              isPublicMode
                ? COMPOSER_PLACEHOLDER_DEFAULT
                : getComposerBrandGatePlaceholder({
                    isActiveBrandReady,
                    hasReadyBrand,
                  })
            }
            value={currentInput}
            data-testid="prompt-textarea"
          />
          <PromptInputFooter>
            <PromptInputTools>
              <PromptInputActionMenuMobile allowImageUrl={!isPublicMode} />
              {isPublicMode ? null : <PromptInputDictationButton />}
            </PromptInputTools>

            <ComposerSubmitButton
              input={currentInput}
              status={isNavigating ? 'submitted' : 'ready'}
              disabled={
                !isPublicMode &&
                (isOverCreditLimit || !isActiveBrandReady || isNavigating)
              }
            />
          </PromptInputFooter>
        </PromptInput>
      </div>
    </div>
  )
}
